import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Accent tones map to the brand palette; text uses the -05 shade (the darkest
 * step, which clears AA contrast for small text on the -10 tint), while the
 * lighter -03 drives the background and border tints. `neutral` is the
 * warm-gray chip for non-accent states. */
export type StatusTone = "green" | "maroon" | "orange" | "violet" | "accent" | "neutral";

const TONE: Record<StatusTone, string> = {
  green: "bg-jj-green-03/10 text-jj-green-05 border-jj-green-03/30",
  maroon: "bg-jj-maroon-05/10 text-jj-maroon-05 border-jj-maroon-05/30",
  orange: "bg-jj-orange-03/10 text-jj-orange-05 border-jj-orange-03/30",
  violet: "bg-jj-violet-03/10 text-jj-violet-05 border-jj-violet-03/30",
  accent: "bg-primary/10 text-primary border-primary/30",
  neutral: "bg-warm-gray-04/40 text-warm-gray-08 border-warm-gray-05",
};

interface StatusPillProps {
  tone?: StatusTone;
  children: ReactNode;
  className?: string;
  /** Native tooltip surfaced on hover (e.g. to explain a compact chip). */
  title?: string;
}

/** Compact monospaced status chip shared across tables and dashboards. */
export function StatusPill({ tone = "neutral", children, className, title }: StatusPillProps) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] font-medium tracking-wide",
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
